// =============================================================================
// Routes — root section registry
// =============================================================================
// Single source of truth for all client-side routes in the app.
// Each route group is defined in its own file under src/routes/sections/
// and spread into the final routesSection array here.
//
// Route groups:
//   authRoutes      — /auth/amplify/* (sign-in, sign-up, verify, reset, update)
//   dashboardRoutes — /dashboard
//   newCmaRoutes    — /new-cma
//   cmaResultsRoutes— /cma-results/:requestId
//   usersRoutes     — /users
//   profileRoutes   — /profile
//   securityRoutes  — /security
//   billingRoutes   — /billing
//   Legal pages     — /privacy-policy, /terms
//   404             — catch-all → Page404
//
// Auth guard:
//   Protected routes use AuthGuard (src/auth/guard/auth-guard.tsx) which
//   redirects unauthenticated users to /auth/amplify/sign-in.
//
// Related files:
//   src/routes/paths.ts          → all path constants
//   src/routes/sections/*.tsx    → individual route definitions
// =============================================================================

import type { RouteObject } from 'react-router';

import { Navigate } from 'react-router';
import { lazy, lazy as lazyLegal } from 'react';

import { CONFIG } from 'src/global-config';

import { authRoutes } from './auth';
import { usersRoutes } from './users';
import { newCmaRoutes } from './new-cma';
import { billingRoutes } from './billing';
import { profileRoutes } from './profile';
import { securityRoutes } from './security';
import { dashboardRoutes } from './dashboard';
import { cmaHistoryRoutes } from './cma-history';
import { cmaResultsRoutes } from './cma-results';

const PrivacyPolicyPage = lazyLegal(() => import('src/pages/privacy-policy'));
const TermsPage = lazyLegal(() => import('src/pages/terms'));

// ----------------------------------------------------------------------

const Page404 = lazy(() => import('src/pages/error/404'));

export const routesSection: RouteObject[] = [
  {
    path: '/',
    element: <Navigate to={CONFIG.auth.redirectPath} replace />,
  },

  // Auth
  ...authRoutes,

  // Dashboard
  ...dashboardRoutes,

  // New CMA
  ...newCmaRoutes,

  // CMA History
  ...cmaHistoryRoutes,

  // CMA Results
  ...cmaResultsRoutes,

  // Users
  ...usersRoutes,

  // Profile
  ...profileRoutes,

  // Security
  ...securityRoutes,

  // Billing — registers /billing (BillingView, behind AuthGuard); see
  // ./billing.tsx for the route definition and src/sections/billing/
  // BillingView.tsx for what actually renders there + how it talks to
  // the cmpr-stripe-checkout Lambda.
  ...billingRoutes,

  // Legal
  { path: 'privacy-policy', element: <PrivacyPolicyPage /> },
  { path: 'terms', element: <TermsPage /> },

  // No match
  { path: '*', element: <Page404 /> },
];
